import React, { useState } from 'react';
import { FaShare, FaCopy, FaCheck } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { setDocumentShare } from '../../redux/slices/documentShareSlice';
import { toast } from 'react-toastify';

interface DocumentShareButtonProps {
  documentID: string;
  orgId: string;
  documentName?: string;
}

interface ShareResponse {
  shareId: string;
  shareUrl: string;
  isShared: boolean;
  sharedAt: string;
}

const DocumentShareButton: React.FC<DocumentShareButtonProps> = ({
  documentID,
  orgId,
  documentName = 'Document'
}) => {
  const [showModal, setShowModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const dispatch = useDispatch();

  // Check if document is already shared on component mount
  React.useEffect(() => {
    const checkShareStatus = async () => {
      try {
        const authToken = sessionStorage.getItem('authToken');
        if (!authToken) return;
        
        const API_URL = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001';
        
        const response = await fetch(`${API_URL}/api/v1/data/documents/${documentID}/share-status`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.isShared && data.shareId) {
            setIsShared(true);
            const link = `${window.location.origin}/document/shared/${data.shareId}?docId=${documentID}&orgId=${orgId}`;
            setShareLink(link);
          } else {
            setIsShared(false);
            setShareLink('');
          }
        } else {
          setIsShared(false);
          setShareLink('');
        }
      } catch (error) {
        console.error('Error checking share status:', error);
        setIsShared(false);
        setShareLink('');
      }
    };
    
    if (documentID && orgId) {
      checkShareStatus();
    }
  }, [documentID, orgId]);

  const revokeShare = async () => {
    setIsRevoking(true);
    try {
      const authToken = sessionStorage.getItem('authToken');
      const API_URL = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001';
      
      const response = await fetch(`${API_URL}/api/v1/data/documents/${documentID}/share`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to revoke share');
      }
      
      setIsShared(false);
      setShareLink('');
      toast.success('Document share has been revoked');
      setShowModal(false);
    } catch (error) {
      console.error('Error revoking share:', error);
      toast.error('Failed to revoke share. Please try again.');
    } finally {
      setIsRevoking(false);
    }
  };

  const generateShareLink = async () => {
    setIsGenerating(true);
    try {
      const authToken = sessionStorage.getItem('authToken');
      const API_URL = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001';
      
      const response = await fetch(`${API_URL}/api/v1/data/documents/${documentID}/share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ orgId })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create share link');
      }
      
      const data = await response.json();
      const link = `${window.location.origin}/document/shared/${data.shareId}?docId=${documentID}&orgId=${orgId}`;
      
      dispatch(setDocumentShare({
        docId: documentID,
        orgId: orgId,
        shareId: data.shareId
      }));
      
      setShareLink(link);
      setIsShared(true);
      toast.success('Document share link generated successfully!');
    } catch (error) {
      console.error('Error generating share link:', error);
      toast.error('Failed to generate share link. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success('Share link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy link. Please try again.');
    }
  };

  const handleShareClick = () => {
    setShowModal(true);
    if (!isShared) {
      generateShareLink();
    }
  };

  return (
    <>
      <button
        onClick={handleShareClick}
        className={`flex items-center gap-2 px-2 py-2 sm:px-4 rounded-md transition-colors text-base font-medium ${
          isShared 
            ? 'bg-red-600 text-white hover:bg-red-700' 
            : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
        title={isShared ? "Document Shared" : "Share Document"}
      >
        <FaShare className="w-4 h-4" />
        <span className="hidden sm:inline">{isShared ? 'Shared' : 'Share'}</span>
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">{isShared ? 'Document Share' : 'Share Document'}</h3>
            <p className="text-gray-600 mb-4">
              {isShared 
                ? `"${documentName}" is currently shared. Copy the link or revoke access below.`
                : `Share "${documentName}" with others. Anyone with this link can view the document.`
              }
            </p>
            
            {isShared && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-700">
                  <FaCheck className="w-4 h-4" />
                  <span className="font-medium">Document is now shared!</span>
                </div>
                <p className="text-sm text-green-600 mt-1">
                  Your document is accessible via the share link below.
                </p>
              </div>
            )}
            
            {isGenerating ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-2">Generating share link...</span>
              </div>
            ) : shareLink ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded border">
                  <input
                    type="text"
                    value={shareLink}
                    readOnly
                    className="flex-1 bg-transparent text-sm text-gray-700 outline-none"
                  />
                  <button
                    onClick={copyToClipboard}
                    className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                  >
                    {copied ? <FaCheck className="w-3 h-3" /> : <FaCopy className="w-3 h-3" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  This link allows read-only access to your document. Edit controls will be hidden for viewers.
                </p>
                
                {isShared && (
                  <button
                    onClick={revokeShare}
                    disabled={isRevoking}
                    className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {isRevoking ? 'Revoking...' : 'Revoke Share'}
                  </button>
                )}
              </div>
            ) : null}
            
            <div className="flex gap-3 justify-end mt-6">
              <button 
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DocumentShareButton;